import * as React from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as AccessControlGroupsState from "../../store/AccessControlGroups";
import { PageControl } from "./PageControl";
import { debounce } from "lodash";
import { defaultPageSize } from "../../helpers";
import { AccessControlGroupResponse } from "../../api/ApiClient";
import { Spinner } from "./Spinner";
import AccessControlGroupsFilters from "components/AccessControlGroups/Filters";
import { useSnackbar } from "notistack";
import { Button } from "components/ui/button";

const TransformedType = (type: string | undefined): string => {
  if (!type) return "";

  switch (type) {
    case "regular":
      return "Regular";
    case "defaultForProperty":
      return "Default For Property";
    case "unit":
      return "Unit";
    case "defaultForGuests":
      return "Default For Guests";
    case "defaultForStaff":
      return "Default For Staff";
    case "staffOnly":
      return "Staff Only";
    default:
      return type; // Return original value if no match
  }
};

interface Injected {
  accessControlGroups: AccessControlGroupsState.AccessControlGroupsState;
}

type OwnProps = {
  initialTyped?: string;
  onSelected: (site: AccessControlGroupResponse) => any;
  pageSize?: number;
  showFirst?: number | null | undefined;
  selectedAccessControlGroups?: Array<AccessControlGroupResponse>;
  renderButton?: (site: AccessControlGroupResponse) => any;
  selectedLabel?: string;
  selectedBtnClass?: string;
  unselectedLabel?: string;
  unselectedBtnClass?: string;
  btnStyle?: Object;
};

interface SnackProps {
  enqueueSnackbar: (node: React.ReactNode, options?: any) => void;
  closeSnackbar: (key?: any) => void;
}

type Props = Injected &
  typeof AccessControlGroupsState.actionCreators &
  OwnProps &
  SnackProps;

interface State {
  typedAccount: string;
  groupType: string;
  propertyExternalId: string;
}

class SelectAccessControlGroup extends React.Component<Props, State> {
  state: State = {
    typedAccount: this.props.initialTyped || "",
    groupType: "",
    propertyExternalId: "",
  };

  componentDidMount() {
    console.log("SelectAccessControlGroup: Requesting accessControlGroups...");
    this.refresh();
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.pageSize !== prevProps.pageSize) {
      this.refresh();
    }
  }

  refresh = async () => {
    try {
      const { pageSize } = this.props;
      const {
        typedAccount: searchTerm,
        propertyExternalId,
        groupType,
      } = this.state;
      await this.props.setFetchParamAccessControlGroups({
        page: 0,
        pageSize: pageSize || defaultPageSize,
        searchTerm,
        groupType,
        propertyExternalId,
      });
    } catch (e) {
      console.error("Error requesting accessControlGroups", e);
      let errorMessage = "Error requesting access control groups";
      try {
        const err: any = e;
        if (err?.response) {
          const data =
            typeof err.response === "string"
              ? JSON.parse(err.response)
              : err.response;
          if (data?.errors?.length) {
            errorMessage = data.errors.map((x: any) => x.message).join(", ");
          } else if (data?.message) {
            errorMessage = data.message;
          }
        } else if (err?.message) {
          errorMessage = err.message;
        }
      } catch {
        // ignore parse errors
      }
      this.props.enqueueSnackbar &&
        this.props.enqueueSnackbar(
          <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
            {errorMessage}
          </p>,
          { variant: "error" },
        );
    }
  };

  handleTypedAccount = (typedAccount: string) => {
    this.setState({ typedAccount }, this.searchAccount);
  };

  handleGroupType = (groupType: string) => {
    this.setState({ groupType }, this.searchAccount);
  };

  handlePropertyExternalId = (propertyExternalId: string) => {
    this.setState({ propertyExternalId }, this.searchAccount);
  };

  searchAccount = debounce(this.refresh, 1000);

  public render() {
    const { accessControlGroups, searchTerm, isLoading } =
      this.props.accessControlGroups;

    const { typedAccount, groupType, propertyExternalId } = this.state;

    return (
      <div>
        <AccessControlGroupsFilters
          typedAccount={typedAccount}
          handleTypedAccount={this.handleTypedAccount}
          groupType={groupType}
          handleGroupType={this.handleGroupType}
          propertyExternalId={propertyExternalId}
          handlePropertyExternalId={this.handlePropertyExternalId}
        />
        <h4>
          {searchTerm ? `AccessControlGroups Matching '${searchTerm}'` : ""}
          {isLoading && <Spinner />}
        </h4>
        {this.renderAccessControlGroupsTable(accessControlGroups)}
      </div>
    );
  }

  private defaultRenderButton = (
    accessControlGroup: AccessControlGroupResponse,
  ) => {
    const {
      selectedAccessControlGroups,
      onSelected,
      btnStyle,
      selectedBtnClass,
      selectedLabel,
      unselectedBtnClass,
      unselectedLabel,
    } = this.props;
    const isSelected =
      selectedAccessControlGroups &&
      selectedAccessControlGroups.find((a) => a.id === accessControlGroup.id);
    const className = isSelected
      ? selectedBtnClass || "btn-primary"
      : unselectedBtnClass || "btn-default";
    return (
      <Button
        className={className}
        type="button"
        style={btnStyle as React.CSSProperties | undefined}
        variant={isSelected ? "default" : "outline"}
        onClick={() => onSelected(accessControlGroup)}
      >
        {!isSelected
          ? unselectedLabel || "Select"
          : selectedLabel || "Unselect"}
      </Button>
    );
  };

  private renderAccessControlGroupsTable(
    accessControlGroups: AccessControlGroupResponse[],
  ) {
    const { renderButton, showFirst } = this.props;
    const { isLoading } = this.props.accessControlGroups;
    accessControlGroups = !showFirst
      ? accessControlGroups
      : (accessControlGroups || []).slice(0, showFirst);

    return (
      <div>
        <table className="table table-hover">
          <thead>
            <tr>
              <th>Name</th>
              <th>ExternalId</th>
              <th>propertyExternalId</th>
              <th>UnitNumber</th>
              <th>Type</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {accessControlGroups.map((accessControlGroup, i) => (
              <tr key={"f_" + i}>
                <td>{accessControlGroup.name}</td>
                <td>{accessControlGroup.externalId}</td>
                <td>{accessControlGroup.propertyExternalId}</td>
                <td>{accessControlGroup.unitNumber}</td>
                <td>{TransformedType(accessControlGroup.type)}</td>
                <td style={{ width: 100 }}>
                  {renderButton
                    ? renderButton(accessControlGroup)
                    : this.defaultRenderButton(accessControlGroup)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!showFirst && (
          <PageControl
            onNext={this.props.nextAccessControlGroupPage}
            onPrev={this.props.prevAccessControlGroupPage}
            currentPage={this.props.accessControlGroups.page}
            pageSize={this.props.pageSize || defaultPageSize}
            setPageSize={this.props.setPageSize}
            isLoading={isLoading}
            disablePrev={this.props.accessControlGroups.page === 0}
            disableNext={
              this.props.accessControlGroups.accessControlGroups.length <
              this.props.accessControlGroups.pageSize
            }
          />
        )}
      </div>
    );
  }
}

const ConnectedSelectAccessControlGroup = connect<
  any,
  any,
  OwnProps,
  ApplicationState
>(
  (state) => ({
    accessControlGroups: state.accessControlGroups,
  }),
  { ...AccessControlGroupsState.actionCreators },
)(SelectAccessControlGroup);

const SelectAccessControlGroupWithSnackbar: React.FC<any> = (props) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  return (
    <ConnectedSelectAccessControlGroup
      {...props}
      enqueueSnackbar={enqueueSnackbar}
      closeSnackbar={closeSnackbar}
    />
  );
};

export default SelectAccessControlGroupWithSnackbar;
